import { Editor } from '@tiptap/core';

export const getEditorExtensionOptions = (
  editor: Editor,
  extensionName: string,
) => {
  const extension = editor.extensionManager.extensions.find(
    (ext) => ext.name === extensionName,
  );
  const options = extension?.options ?? {};
  return options;
};

export const getEditorSelectedHTML = (editor: Editor): string => {
  if (typeof window === 'undefined') {
    return '';
  }
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return '';
  }
  const range = selection?.getRangeAt(0);
  const container = document.createElement('div');
  container.appendChild(range.cloneContents());
  return container.innerHTML;
};

export const getEditorSelectedText = (editor: Editor): string => {
  const selectedText =
    editor?.state?.doc?.textBetween(
      editor.state.selection.from,
      editor.state.selection.to,
      '\n',
    ) || '';
  return selectedText;
};

/**
 * Clean up Markdown string by removing empty list items.
 * by filtering out any line that consists solely of a list marker (e.g., "-", "*", "+", or numeric lists like "1."),
 * @param markdownStr - The raw markdown string to clean.
 * @returns A new markdown string with empty list items removed.
 */
export const removeEmptyListItems = (markdownStr: string): string => {
  return (
    markdownStr
      .split('\n')
      // Remove lines that are just a list marker (bullet or numbered) with no content
      .filter((line) => !/^\s*(?:[-*+]|\d+[.)])\s*$/.test(line))
      .join('\n')
  );
};

/**
 * Converts HTML string to plain text while preserving Markdown formatting
 * Removes HTML span tags while keeping the text content and Markdown formatting
 */
export const stripHtmlTags = (html: string): string => {
  // Create a temporary DOM element to parse the HTML
  const tempElement = document.createElement('div');
  tempElement.innerHTML = html;

  // Remove all span elements while preserving their content
  const spanElements = tempElement.querySelectorAll('span');
  spanElements.forEach((span) => {
    // Replace span with its text content
    const textNode = document.createTextNode(span.textContent ?? '');
    span.parentNode?.replaceChild(textNode, span);
  });

  // Get HTML content which now has spans removed but preserves other formatting
  const htmlWithoutSpans = tempElement.innerHTML;

  // For text that is inside a span but has markdown formatting (**bold**, lists, etc),
  // we want to preserve that formatting
  return htmlWithoutSpans;
};
